import { dbManager } from './db_manager';

// Import Supabase for reliable user detection
import { supabase } from './supabaseClient';

const getCurrentUser = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.user || null;
    } catch { return null; }
};

export interface NoteCard {
    id: string;              // UUID/Deterministic ID
    bookId?: string;         // Associated book ID
    original: string;        // The phrase/original text
    translation: string;     // The user's translation
    langCode: string;        // e.g. 'en-US', 'fr-FR'
    category?: string;       // Full language name or deck name
    createdAt: number;
    deck_id?: string;
}

const PRONUNCIATION_KEY = 'libritui_notes_pronunciation';
const WRITING_KEY = 'libritui_notes_writing';

const ISO_TO_LOCALE: Record<string, string> = {
    'en': 'en-US', 'fr': 'fr-FR', 'de': 'de-DE', 'pt': 'pt-BR',
    'it': 'it-IT', 'es': 'es-ES', 'nl': 'nl-NL', 'ja': 'ja-JP',
    'ru': 'ru-RU', 'zh': 'zh-CN'
};

export const normalizeLangCode = (raw: string | undefined): string => {
    if (!raw) return 'en-US';
    const lower = raw.toLowerCase().split(/[-_]/)[0];
    return ISO_TO_LOCALE[lower] || ISO_TO_LOCALE[raw.toLowerCase()] || raw;
};

const load = (key: string): NoteCard[] => {
    try {
        const data = localStorage.getItem(key);
        return data ? (JSON.parse(data) as NoteCard[]) : [];
    } catch {
        return [];
    }
};

const saveLocally = (key: string, cards: NoteCard[]): void => {
    localStorage.setItem(key, JSON.stringify(cards));
};

const syncToD1 = async (type: 'pronunciation' | 'writing', card: NoteCard) => {
    const user = await getCurrentUser();
    if (!user) return;

    const table = type === 'pronunciation' ? 'user_pronunciation' : 'user_writing';
    
    // Explicitly send 'id' as per user request to facilitate deletion
    await dbManager.from(table, user.id).upsert({
        id: card.id,
        user_id: user.id,
        book_id: card.bookId ? String(card.bookId) : '',
        original: card.original,
        translation: card.translation,
        lang_code: card.langCode, // Column in D1 is lang_code
        category: card.category || null,
        deck_id: card.deck_id || null,
        created_at: new Date(card.createdAt).toISOString()
    });

    console.log(`[notesDeckManager] Synced ${type} to D1:`, card.original);
};

const removeFromD1 = async (type: 'pronunciation' | 'writing', id: string) => {
    const user = await getCurrentUser();
    if (!user) return;

    const table = type === 'pronunciation' ? 'user_pronunciation' : 'user_writing';
    await dbManager.from(table, user.id).delete(id);
};

const addCard = async (
    type: 'pronunciation' | 'writing',
    original: string,
    translation: string,
    langCode: string,
    category?: string,
    bookId?: string,
    deckId?: string
): Promise<void> => {
    const key = type === 'pronunciation' ? PRONUNCIATION_KEY : WRITING_KEY;
    const cards = load(key);

    const user = await getCurrentUser();
    const userId = user ? user.id : 'guest';

    const safeBtoa = (str: string) => btoa(unescape(encodeURIComponent(str)));
    // Use timestamp + random to ensure true uniqueness (SUM logic)
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 7);
    const uniqueId = `${safeBtoa(`${userId}_${original.trim()}`).slice(0, 20)}_${timestamp}_${random}`;

    const newCard: NoteCard = {
        id: uniqueId,
        bookId,
        original: original.trim(),
        translation: translation.trim(),
        langCode,
        category,
        deck_id: deckId,
        createdAt: Date.now()
    };

    cards.unshift(newCard);
    saveLocally(key, cards);
    try {
        await syncToD1(type, newCard);
    } catch (err) {
        console.error(`Failed to sync ${type} to D1:`, err);
        throw err; // Re-throw to allow queue detection
    }

    window.dispatchEvent(new CustomEvent('notesDeckUpdated', { detail: { key } }));
};

export const notesDeckManager = {
    addToPronunciation: async (original: string, translation: string, langCode: string, category?: string, bookId?: string, deckId?: string): Promise<void> => {
        await addCard('pronunciation', original, translation, langCode, category, bookId, deckId);
    },

    addToWriting: async (original: string, translation: string, langCode: string, category?: string, bookId?: string, deckId?: string): Promise<void> => {
        await addCard('writing', original, translation, langCode, category, bookId, deckId);
    },

    getPronunciationNotes: (): NoteCard[] => load(PRONUNCIATION_KEY),
    getWritingNotes: (): NoteCard[] => load(WRITING_KEY),

    groupByLang: (cards: NoteCard[]): Record<string, NoteCard[]> => {
        const result: Record<string, NoteCard[]> = {};
        cards.forEach(card => {
            const groupKey = card.category || card.langCode;
            if (!result[groupKey]) result[groupKey] = [];
            result[groupKey].push(card);
        });
        return result;
    },

    removePronunciationNote: async (id: string | number): Promise<void> => {
        const cards = load(PRONUNCIATION_KEY).filter(c => c.id !== String(id));
        saveLocally(PRONUNCIATION_KEY, cards);
        try {
            await removeFromD1('pronunciation', String(id));
        } catch (err) {
            console.error("Failed to delete from D1:", err);
        }
    },

    removeWritingNote: async (id: string | number): Promise<void> => {
        const cards = load(WRITING_KEY).filter(c => c.id !== String(id));
        saveLocally(WRITING_KEY, cards);
        try {
            await removeFromD1('writing', String(id));
        } catch (err) {
            console.error("Failed to delete from D1:", err);
        }
    },

    deletePronunciationDeck: async (categoryOrLang: string): Promise<void> => {
        const cards = load(PRONUNCIATION_KEY);
        const remaining = cards.filter(c => (c.category || c.langCode) !== categoryOrLang);
        
        saveLocally(PRONUNCIATION_KEY, remaining);
        
        const user = await getCurrentUser();
        if (user) {
            // Background deletion
            dbManager.from('user_pronunciation', user.id).deleteMatch({ category: categoryOrLang }).catch(() => {});
            dbManager.from('user_pronunciation', user.id).deleteMatch({ lang_code: categoryOrLang }).catch(() => {});
        }
        window.dispatchEvent(new CustomEvent('notesDeckUpdated', { detail: { key: PRONUNCIATION_KEY } }));
    },

    deleteWritingDeck: async (categoryOrLang: string): Promise<void> => {
        const cards = load(WRITING_KEY);
        const remaining = cards.filter(c => (c.category || c.langCode) !== categoryOrLang);
        
        saveLocally(WRITING_KEY, remaining);
        
        const user = await getCurrentUser();
        if (user) {
            // Background deletion
            dbManager.from('user_writing', user.id).deleteMatch({ category: categoryOrLang }).catch(() => {});
            dbManager.from('user_writing', user.id).deleteMatch({ lang_code: categoryOrLang }).catch(() => {});
        }
        window.dispatchEvent(new CustomEvent('notesDeckUpdated', { detail: { key: WRITING_KEY } }));
    },

    syncAllFromD1: async () => {
        const user = await getCurrentUser();
        if (!user) return;

        try {
            // AÇÃO: Execução Sequencial com Delay para evitar 429
            const pResults = await dbManager.from('user_pronunciation', user.id).select();
            
            // Pequeno intervalo entre tabelas para evitar 429
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const wResults = await dbManager.from('user_writing', user.id).select();

            if (pResults) {
                const pCards = pResults.map((d: any) => ({
                    id: (d.id || '').toString(),
                    bookId: (d.book_id || '').toString(),
                    original: (d.original || '').toString(),
                    translation: (d.translation || '').toString(),
                    langCode: (d.lang_code || '').toString(),
                    category: (d.category || '').toString(),
                    createdAt: new Date(d.created_at || Date.now()).getTime()
                }));
                saveLocally(PRONUNCIATION_KEY, pCards);
            }

            if (wResults) {
                const wCards = wResults.map((d: any) => ({
                    id: (d.id || '').toString(),
                    bookId: (d.book_id || '').toString(),
                    original: (d.original || '').toString(),
                    translation: (d.translation || '').toString(),
                    langCode: (d.lang_code || '').toString(),
                    category: (d.category || '').toString(),
                    createdAt: new Date(d.created_at || Date.now()).getTime()
                }));
                saveLocally(WRITING_KEY, wCards);
            }
        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                console.error("Failed to sync from D1:", err);
            }
        }
    }
};
